import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { ReportQueryKeys } from "./keys";
import { ICreateReport } from "../../models/Reports/CreateReport";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IUpdateReport } from "../../models/Reports/UpdateReport";
import { IUpdateCustomerReportLinks } from "../../models/Reports/CustomerReportLink";

export default function useReportMutations() {
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Report");
  const { post, put, del } = useApiHelper();

  const create = useMutation({
    mutationFn: (report: ICreateReport) => post(`/report`, report),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ReportQueryKeys.all() });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const update = useMutation({
    mutationFn: (updatedReport: IUpdateReport) =>
      put(`/report/${updatedReport.id}`, updatedReport),
    onSuccess: (_, updateInput) => {
      queryClient.invalidateQueries({ queryKey: ReportQueryKeys.all() });
      queryClient.invalidateQueries({ queryKey: ReportQueryKeys.single(updateInput.id) });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const remove = useMutation({
    mutationFn: (reportId: string) => del(`/report/${reportId}`),
    onSuccess: (_, deleteInput) => {
      queryClient.invalidateQueries({ queryKey: ReportQueryKeys.all() });
      queryClient.invalidateQueries({ queryKey: ReportQueryKeys.single(deleteInput) });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const updateCustomerReportLinks = useMutation({
    mutationFn: (request: IUpdateCustomerReportLinks & { reportId?: string }) => {
      const { reportId, ...linksRequest } = request;
      return post(`/report/${reportId}/customer-links`, linksRequest);
    },
    onSuccess: (_, variables) => {
      // Invalidate all customer reports queries since links may have changed
      queryClient.invalidateQueries({ queryKey: ReportQueryKeys.all() });
      // Invalidate the specific report's customer links if reportId is provided
      if (variables.reportId) {
        queryClient.invalidateQueries({ queryKey: ReportQueryKeys.reportCustomerLinks(variables.reportId) });
      }
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  return {
    create,
    update,
    remove,
    updateCustomerReportLinks,
  };
}
